import { useEffect } from 'react';
import { getSocket } from '@/net/socket';
import { useAuthStore } from '@/auth/authStore';
import { useToasts } from '@/stores/toastStore';
import { useClanNotifStore } from '@/stores/clanNotifStore';
import { useClanStore } from '@/stores/clanStore';
import { clanApi } from '@/clan/clanApi';
import { clanNotifText } from '@/clan/clanNotifText';
import { useT } from '@/i18n/useI18n';
import type { ClanNotifPayload } from '@shared/protocol';

/**
 * Always-mounted listener that keeps the clan inbox + unread badge in sync.
 * Hydrates from the server on load/reconnect, and turns live `clan:notif`
 * socket pushes into a toast + an unread bump (drives the Home red dot).
 */
export function ClanNotifier(): null {
  const token = useAuthStore((s) => s.token);
  const enabled = useClanStore((s) => s.enabled);
  const loadAvailability = useClanStore((s) => s.loadAvailability);
  const t = useT();

  useEffect(() => {
    if (!token) return;
    const refresh = (): void => { void loadAvailability(); };
    refresh();
    const intervalId = window.setInterval(refresh, 30_000);
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [loadAvailability, token]);

  useEffect(() => {
    if (!token || !enabled) return;
    const socket = getSocket();

    const hydrate = (): void => {
      clanApi
        .notifications()
        .then((r) => useClanNotifStore.getState().hydrate(r.items, r.unread))
        .catch(() => { /* offline — keep the persisted snapshot */ });
    };

    const onNotif = (p: ClanNotifPayload): void => {
      useClanNotifStore.getState().pushLive({ type: p.type, data: p.data, createdAt: p.createdAt });
      const { icon, text } = clanNotifText(p.type, p.data, t);
      if (text) {
        useToasts.getState().push({ kind: 'info', title: `${icon} ${t('clan.title')}`, body: text });
      }
      // Any clan event reflects a server-side change worth re-fetching live:
      // officers get the new request in their list (join_request), members
      // reconcile membership (approve/kick/reject) — no need to reopen the screen.
      void useClanStore.getState().refresh();
    };

    const onRefresh = (): void => { void useClanStore.getState().refresh(); };

    void useClanStore.getState().refresh();
    hydrate();
    socket.on('connect', hydrate);
    socket.on('clan:notif', onNotif);
    socket.on('clan:refresh', onRefresh);
    return () => {
      socket.off('connect', hydrate);
      socket.off('clan:notif', onNotif);
      socket.off('clan:refresh', onRefresh);
    };
  }, [enabled, token, t]);

  return null;
}
